import { DERIVED_STAT_ABBRS, MISSING_STAT, RATE_FORMULAS } from '../constants'
import type { StatValue } from '../types'

const SV_PCT_ABBR = 'SV%'
const GOALS_AGAINST_ABBR = 'GA'
const SAVES_ABBR = 'SV'

export const getStatValue = (
  stats: StatValue[] | undefined,
  abbr: string
): string | undefined => {
  const found = stats?.find((s) => (s.abbr ?? s.type?.abbr) === abbr)
  const value = found?.value
  if (value === undefined || value === null) {
    return undefined
  }
  const text = String(value)
  return text === '' || text === MISSING_STAT ? undefined : text
}

export const toStatNumber = (value: string | undefined): number | null => {
  if (value === undefined) {
    return null
  }
  const cleaned = value.replace(/,/g, '').trim()
  if (cleaned === '') {
    return null
  }
  const parsed = Number(cleaned)
  return Number.isFinite(parsed) ? parsed : null
}

export const isRebuildableRate = (abbr: string): boolean =>
  abbr in RATE_FORMULAS

/**
 * A save percentage of 0 with no saves and no goals against means no shots
 * were faced (a real .000 needs at least one goal) — the API still sends 0,
 * which would plot and print as a 0% night. `saves` / `goalsAgainst` are the
 * matching SV / GA, missing read as 0 (the API omits zero stats).
 */
export const isNoShotsSavePct = (
  abbr: string,
  value: number | null,
  saves: number | null,
  goalsAgainst: number | null
): boolean =>
  abbr === SV_PCT_ABBR &&
  value === 0 &&
  (saves ?? 0) === 0 &&
  (goalsAgainst ?? 0) === 0

export const isDerivedStat = (abbr: string): boolean =>
  isRebuildableRate(abbr) ||
  DERIVED_STAT_ABBRS.includes(abbr) ||
  abbr.includes('%') ||
  abbr.includes('/')

/**
 * A `GameStat` row's value. The per-game endpoints omit zero stats, so a
 * missing count stat reads as 0. A missing derived stat stays missing (`–`), as
 * does any stat outside `trackedAbbrs` (the stats its season tracks) when given.
 * Season rows use `getStatValue`, where missing is always `–`.
 */
export const getGameStatValue = (
  stats: StatValue[] | undefined,
  abbr: string,
  trackedAbbrs?: string[]
): string | undefined => {
  const value = getStatValue(stats, abbr)
  if (
    isNoShotsSavePct(
      abbr,
      toStatNumber(value),
      toStatNumber(getStatValue(stats, SAVES_ABBR)),
      toStatNumber(getStatValue(stats, GOALS_AGAINST_ABBR))
    )
  ) {
    return undefined
  }
  if (value !== undefined) {
    return value
  }
  if (isDerivedStat(abbr)) {
    return undefined
  }
  if (trackedAbbrs !== undefined && !trackedAbbrs.includes(abbr)) {
    return undefined
  }
  return '0'
}

export interface RebuildRateOptions {
  /**
   * For per-game formulas only: read a missing numerator as 0 when games > 0
   * (the API omits zero stats, so a pointless month has no `P`). Ratios over
   * another stat (`SV%`) are unaffected.
   */
  missingNumeratorAsZero?: boolean
}

export const rebuildRate = (
  abbr: string,
  sums: Record<string, number>,
  games: number,
  { missingNumeratorAsZero = false }: RebuildRateOptions = {}
): number | null => {
  if (!isRebuildableRate(abbr)) {
    return null
  }
  const { numerator, denominator } = RATE_FORMULAS[abbr]
  const isPerGame = denominator === 'GAMES'
  const top =
    sums[numerator] ?? (isPerGame && missingNumeratorAsZero ? 0 : undefined)
  const bottom = isPerGame ? games : sums[denominator]
  if (top === undefined || !bottom) {
    return null
  }
  const result = top / bottom
  return Number.isFinite(result) ? result : null
}

export const formatStatValue = (value: number | null, abbr: string): string => {
  if (value === null) {
    return MISSING_STAT
  }
  if (abbr === 'SV%') {
    return value.toFixed(3).replace(/^0(?=\.)/, '')
  }
  if (isDerivedStat(abbr)) {
    return value.toFixed(2)
  }
  return Number.isInteger(value) ? String(value) : value.toFixed(2)
}

export const getGamesPlayedAbbr = (abbrs: string[]): 'GP' | 'MP' =>
  abbrs.includes('MP') && !abbrs.includes('GP') ? 'MP' : 'GP'
